// AIAssistantModal.jsx
import { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { fetchChatBotResponse } from "../../redux/Slices/chatBotSlice";
import AiIcon from "../../styles/Assets/ai_icon.png";

const AIAssistantModal = ({
  isOpen,
  onClose,
  initialMessage,
  onSelectResponse,
  clientId,
  coachId,
}) => {
  const dispatch = useDispatch();
  const [isTyping, setIsTyping] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Initialize with first message when modal opens
  useEffect(() => {
    if (isOpen && initialMessage && initialMessage !== "Client submitted a check-in") {
      // Add initial client message
      setMessages([
        {
          id: 1,
          type: "user",
          content: initialMessage,
          timestamp: new Date().toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          }),
        },
      ]);

      // Fetch first AI response
      fetchAIResponse(initialMessage);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialMessage]);

  const fetchAIResponse = async (query) => {
    setIsTyping(true);

    try {
      const refreshToken = localStorage.getItem("Token");

      const response = await dispatch(
        fetchChatBotResponse({
          user_id: clientId,
          coach_id: coachId,
          query: query,
          token: `${refreshToken}`,
          isMessenger: true
        })
      ).unwrap();

      console.log("AI Response:", response);
      const resData = response?.data;

      if (resData) {
        const aiMessages = [];
        let messageId = messages.length + 1;

        // Check for concise, contextual, engaging responses
        if (resData.concise) {
          aiMessages.push({
            id: messageId++,
            type: "ai",
            tone: "concise",
            content: resData.concise,
            timestamp: new Date().toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            }),
          });
        }

        if (resData.contextual) {
          aiMessages.push({
            id: messageId++,
            type: "ai",
            tone: "contextual",
            content: resData.contextual,
            timestamp: new Date().toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            }),
          });
        }

        if (resData.engaging) {
          aiMessages.push({
            id: messageId++,
            type: "ai",
            tone: "engaging",
            content: resData.engaging,
            timestamp: new Date().toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            }),
          });
        }

        // If no concise/contextual/engaging, check for message or generic response
        if (aiMessages.length === 0) {
          const fallbackContent =
            resData.message ||
            (typeof resData === "string" ? resData : null) ||
            "I'm here to help! Could you please rephrase or try again?";

          aiMessages.push({
            id: messageId++,
            type: "ai",
            tone: "generic",
            content: fallbackContent,
            timestamp: new Date().toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            }),
          });
        }

        setMessages((prev) => [...prev, ...aiMessages]);
      }
    } catch (error) {
      console.error("Error fetching AI responses:", error);

      // Add error message
      setMessages((prev) => [
        ...prev,
        {
          id: messages.length + 1,
          type: "ai",
          tone: "error",
          content: "Sorry, I couldn't process your request. Please try again.",
          timestamp: new Date().toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          }),
        },
      ]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleSend = () => {
    if (!inputValue.trim()) return;

    // Add user message
    const userMessage = {
      id: messages.length + 1,
      type: "user",
      content: inputValue,
      timestamp: new Date().toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      }),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue("");

    // Fetch AI response
    fetchAIResponse(inputValue);
  };

  const handleSelectResponse = (messageContent) => {
    onSelectResponse(messageContent);
    handleClose();
  };

  const handleClose = () => {
    setMessages([]);
    setInputValue("");
    onClose();
  };

  const getToneColor = (tone) => {
    switch (tone) {
      case "concise":
        return "bg-blue-50 border-blue-200";
      case "contextual":
        return "bg-green-50 border-green-200";
      case "engaging":
        return "bg-purple-50 border-purple-200";
      case "generic":
        return "bg-gray-50 border-gray-200";
      case "error":
        return "bg-red-50 border-red-200";
      default:
        return "bg-white border-gray-200";
    }
  };

  const getToneBadgeColor = (tone) => {
    switch (tone) {
      case "concise":
        return "bg-blue-500";
      case "contextual":
        return "bg-green-500";
      case "engaging":
        return "bg-purple-500";
      case "generic":
        return "bg-gray-500";
      default:
        return "bg-gray-400";
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-white rounded-t-lg">
          <div className="flex items-center gap-3">
            {/* <div className="w-10 h-10 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center"> */}
              <img src={AiIcon} alt="AI" className="w-6 h-6" />
            {/* </div> */}
            <div>
              <h2 className="text-lg font-semibold text-gray-800">
                AI Assistant
              </h2>
              {/* <p className="text-xs text-gray-500">
                Helping you respond to clients
              </p> */}
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition p-1"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Messages Container - Similar to AIAssistanceChat */}
        <div className="messages-container">
          {messages.map((message) => (
            <div key={message.id} className="message">
              {message.type === "ai" ? (
                <div className="message-ai">
                  {/* AI Avatar */}
                  <div className="message-avatar avatar-ai">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <rect
                        x="6"
                        y="6"
                        width="4"
                        height="4"
                        fill="currentColor"
                      />
                      <rect
                        x="14"
                        y="6"
                        width="4"
                        height="4"
                        fill="currentColor"
                      />
                      <rect
                        x="6"
                        y="14"
                        width="4"
                        height="4"
                        fill="currentColor"
                      />
                      <rect
                        x="14"
                        y="14"
                        width="4"
                        height="4"
                        fill="currentColor"
                      />
                    </svg>
                  </div>

                  <div className="message-content">
                    <div
                      className={`message-bubble cursor-pointer ${getToneColor(
                        message.tone
                      )}`}
                      onClick={() => handleSelectResponse(message.content)}
                    >
                      {/* Tone Badge */}
                      {message.tone &&
                        message.tone !== "generic" &&
                        message.tone !== "error" && (
                          <div className="message-tone-badge">
                            <span
                              className={`text-xs font-semibold text-white px-2 py-1 rounded uppercase ${getToneBadgeColor(
                                message.tone
                              )}`}
                            >
                              {message.tone}
                            </span>
                          </div>
                        )}

                      {/* Message Text */}
                      <p className="message-text">{message.content}</p>

                      {/* Footer: timestamp + click hint */}
                      <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                        <span className="text-xs text-gray-400">
                          {message.timestamp}
                        </span>
                        <span className="text-xs text-blue-500 font-medium">
                          Click to use →
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="message-user">
                  <div className="message-content">
                    <div className="message-bubble message-bubble-user">
                      <p className="message-text message-text-user">
                        {message.content}
                      </p>
                    </div>
                    <span
                      className="message-timestamp"
                      style={{ textAlign: "right", display: "block" }}
                    >
                      {message.timestamp}
                    </span>
                  </div>
                </div>
              )}
            </div>
          ))}

          {/* Typing Indicator */}
          {isTyping && (
            <div className="typing-indicator">
              <div className="message-avatar avatar-ai">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <rect x="6" y="6" width="4" height="4" fill="currentColor" />
                  <rect x="14" y="6" width="4" height="4" fill="currentColor" />
                  <rect x="6" y="14" width="4" height="4" fill="currentColor" />
                  <rect
                    x="14"
                    y="14"
                    width="4"
                    height="4"
                    fill="currentColor"
                  />
                </svg>
              </div>
              <div className="typing-dots">
                <div className="typing-dot"></div>
                <div className="typing-dot"></div>
                <div className="typing-dot"></div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* <div className="message-content">
          {messages.map((message) => (
            <div key={message.id} className="message">
              {message.type === "ai" ? (
                <div className="message-ai">
                  <div className="flex items-start gap-2">
                    <div className="message-avatar avatar-ai w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center flex-shrink-0">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <rect x="6" y="6" width="4" height="4" fill="white" />
                        <rect x="14" y="6" width="4" height="4" fill="white" />
                        <rect x="6" y="14" width="4" height="4" fill="white" />
                        <rect x="14" y="14" width="4" height="4" fill="white" />
                      </svg>
                    </div>

                    <div className="flex-1">
                      <div
                        className={`message-bubble border rounded-lg p-3 cursor-pointer hover:shadow-md transition-all ${getToneColor(
                          message.tone
                        )}`}
                        onClick={() => handleSelectResponse(message.content)}
                      >
                        {message.tone &&
                          message.tone !== "generic" &&
                          message.tone !== "error" && (
                            <div className="flex items-center gap-2 mb-2">
                              <span
                                className={`text-xs font-semibold text-white px-2 py-1 rounded uppercase ${getToneBadgeColor(
                                  message.tone
                                )}`}
                              >
                                {message.tone}
                              </span>
                            </div>
                          )}

                        <p className="text-sm text-gray-800 leading-relaxed mb-2">
                          {message.content}
                        </p>

                        <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                          <span className="text-xs text-gray-400">
                            {message.timestamp}
                          </span>
                          <span className="text-xs text-blue-500 font-medium">
                            Click to use →
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="message-user flex justify-end">
                  <div className="message-content max-w-[80%]">
                    <div className="message-bubble bg-blue-500 text-white rounded-lg rounded-br-none p-3 shadow">
                      <p className="text-sm leading-relaxed">
                        {message.content}
                      </p>
                      <span className="block text-xs text-blue-100 text-right mt-1">
                        {message.timestamp}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}

          {isTyping && (
            <div className="typing-indicator flex items-start gap-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center flex-shrink-0">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <rect x="6" y="6" width="4" height="4" fill="white" />
                  <rect x="14" y="6" width="4" height="4" fill="white" />
                  <rect x="6" y="14" width="4" height="4" fill="white" />
                  <rect x="14" y="14" width="4" height="4" fill="white" />
                </svg>
              </div>
              <div className="typing-dots bg-white rounded-lg p-3 shadow flex items-center gap-1">
                <div className="typing-dot w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                <div
                  className="typing-dot w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                  style={{ animationDelay: "0.1s" }}
                ></div>
                <div
                  className="typing-dot w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                  style={{ animationDelay: "0.2s" }}
                ></div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div> */}

        {/* Input Area - Similar to AIAssistanceChat */}
        <div className="border-t bg-white p-4 rounded-b-lg">
          <div className="input-wrapper">
            <input
              type="text"
              className="chat-input"
              placeholder="Ask AI to refine or describe more..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleSend();
                }
              }}
              disabled={isTyping}
            />
            <button
              onClick={handleSend}
              className="send-btn"
              disabled={!inputValue.trim() || isTyping}
            >
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                />
              </svg>
            </button>
          </div>

          <p className="text-xs text-gray-500 mt-2 text-center">
            Click any AI response to use it in your message
          </p>
        </div>
      </div>
    </div>
  );
};

export default AIAssistantModal;
// // AIAssistantModal.jsx
// import React, { useState, useEffect } from "react";
// import { useDispatch } from "react-redux";
// import { Spinner } from "react-bootstrap";
// import { fetchChatBotResponse } from "../../redux/Slices/chatBotSlice";
// import AiIcon from "../../styles/Assets/ai_icon.png";

// const AIAssistantModal = ({
//   isOpen,
//   onClose,
//   initialMessage,
//   onSelectResponse,
//   clientId,
//   coachId,
// }) => {
//   const dispatch = useDispatch();
//   const [isLoading, setIsLoading] = useState(false);
//   const [aiResponses, setAiResponses] = useState({
//     concise: null,
//     contextual: null,
//     engaging: null,
//   });
//   const [selectedResponse, setSelectedResponse] = useState(null);
//   const [followUpQuestion, setFollowUpQuestion] = useState("");
//   const [conversationHistory, setConversationHistory] = useState([]);

//   // Fetch AI responses when modal opens
//   useEffect(() => {
//     if (isOpen && initialMessage) {
//       fetchAIResponses(initialMessage);
//     }
//     console.log('test_selectedUser', clientId)
//   }, [isOpen, initialMessage]);

//   const fetchAIResponses = async (query) => {
//     setIsLoading(true);
//     setAiResponses({ concise: null, contextual: null, engaging: null });

//     try {
//       const refreshToken = localStorage.getItem("Token");

//       const response = await dispatch(
//         fetchChatBotResponse({
//           user_id: clientId,
//           coach_id: coachId,
//           query: query,
//           token: `${refreshToken}`,
//         })
//       ).unwrap();

//       console.log("AI Response:", response);
//       const resData = response?.data;

//       if (resData) {
//         setAiResponses({
//           concise: resData.concise || null,
//           contextual: resData.contextual || null,
//           engaging: resData.engaging || null,
//         });

//         // Add to conversation history
//         setConversationHistory((prev) => [
//           ...prev,
//           {
//             type: "question",
//             text: query,
//           },
//           {
//             type: "responses",
//             concise: resData.concise,
//             contextual: resData.contextual,
//             engaging: resData.engaging,
//           },
//         ]);
//       }
//     } catch (error) {
//       console.error("Error fetching AI responses:", error);
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   const handleFollowUpSubmit = () => {
//     if (!followUpQuestion.trim()) return;

//     fetchAIResponses(followUpQuestion);
//     setFollowUpQuestion("");
//     setSelectedResponse(null);
//   };

//   const handleSelectResponse = (responseType, responseText) => {
//     setSelectedResponse({ type: responseType, text: responseText });
//   };

//   const handleSendToChat = () => {
//     if (selectedResponse) {
//       onSelectResponse(selectedResponse.text);
//       handleClose();
//     }
//   };

//   const handleClose = () => {
//     setAiResponses({ concise: null, contextual: null, engaging: null });
//     setSelectedResponse(null);
//     setFollowUpQuestion("");
//     setConversationHistory([]);
//     onClose();
//   };

//   if (!isOpen) return null;

//   return (
//     <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
//       <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
//         {/* Header */}
//         <div className="flex items-center justify-between p-4 border-b">
//           <div className="flex items-center gap-2">
//             <img src={AiIcon} alt="AI" className="w-6 h-6" />
//             <h2 className="text-lg font-semibold">AI Assistant</h2>
//           </div>
//           <button
//             onClick={handleClose}
//             className="text-gray-500 hover:text-gray-700"
//           >
//             <i className="bi bi-x-lg text-xl"></i>
//           </button>
//         </div>

//         {/* Content */}
//         <div className="flex-1 overflow-y-auto p-4 space-y-4">
//           {/* Original Client Message */}
//           <div className="bg-blue-50 p-3 rounded-lg">
//             <p className="text-xs text-gray-500 mb-1">Client's Message:</p>
//             <p className="text-sm">{initialMessage}</p>
//           </div>

//           {/* Conversation History */}
//           {conversationHistory.map((item, index) => (
//             <div key={index}>
//               {item.type === "question" && index > 0 && (
//                 <div className="bg-gray-100 p-3 rounded-lg mb-3">
//                   <p className="text-xs text-gray-500 mb-1">Your Question:</p>
//                   <p className="text-sm">{item.text}</p>
//                 </div>
//               )}

//               {item.type === "responses" && (
//                 <div className="space-y-3">
//                   {/* Concise Response */}
//                   {item.concise && (
//                     <div
//                       className={`border rounded-lg p-3 cursor-pointer transition-all ${
//                         selectedResponse?.text === item.concise
//                           ? "border-blue-500 bg-blue-50"
//                           : "border-gray-200 hover:border-blue-300"
//                       }`}
//                       onClick={() => handleSelectResponse("concise", item.concise)}
//                     >
//                       <div className="flex items-center justify-between mb-2">
//                         <span className="text-xs font-semibold text-blue-600 uppercase">
//                           Concise
//                         </span>
//                         {selectedResponse?.text === item.concise && (
//                           <i className="bi bi-check-circle-fill text-blue-500"></i>
//                         )}
//                       </div>
//                       <p className="text-sm text-gray-800">{item.concise}</p>
//                     </div>
//                   )}

//                   {/* Contextual Response */}
//                   {item.contextual && (
//                     <div
//                       className={`border rounded-lg p-3 cursor-pointer transition-all ${
//                         selectedResponse?.text === item.contextual
//                           ? "border-green-500 bg-green-50"
//                           : "border-gray-200 hover:border-green-300"
//                       }`}
//                       onClick={() =>
//                         handleSelectResponse("contextual", item.contextual)
//                       }
//                     >
//                       <div className="flex items-center justify-between mb-2">
//                         <span className="text-xs font-semibold text-green-600 uppercase">
//                           Contextual
//                         </span>
//                         {selectedResponse?.text === item.contextual && (
//                           <i className="bi bi-check-circle-fill text-green-500"></i>
//                         )}
//                       </div>
//                       <p className="text-sm text-gray-800">{item.contextual}</p>
//                     </div>
//                   )}

//                   {/* Engaging Response */}
//                   {item.engaging && (
//                     <div
//                       className={`border rounded-lg p-3 cursor-pointer transition-all ${
//                         selectedResponse?.text === item.engaging
//                           ? "border-purple-500 bg-purple-50"
//                           : "border-gray-200 hover:border-purple-300"
//                       }`}
//                       onClick={() => handleSelectResponse("engaging", item.engaging)}
//                     >
//                       <div className="flex items-center justify-between mb-2">
//                         <span className="text-xs font-semibold text-purple-600 uppercase">
//                           Engaging
//                         </span>
//                         {selectedResponse?.text === item.engaging && (
//                           <i className="bi bi-check-circle-fill text-purple-500"></i>
//                         )}
//                       </div>
//                       <p className="text-sm text-gray-800">{item.engaging}</p>
//                     </div>
//                   )}
//                 </div>
//               )}
//             </div>
//           ))}

//           {/* Loading State */}
//           {isLoading && (
//             <div className="flex justify-center items-center py-8">
//               <Spinner animation="border" variant="primary" />
//               <span className="ml-3 text-gray-600">Generating responses...</span>
//             </div>
//           )}

//           {/* Empty State */}
//           {!isLoading &&
//             !aiResponses.concise &&
//             !aiResponses.contextual &&
//             !aiResponses.engaging &&
//             conversationHistory.length === 0 && (
//               <div className="text-center py-8 text-gray-500">
//                 <i className="bi bi-chat-dots text-4xl mb-2"></i>
//                 <p>AI responses will appear here</p>
//               </div>
//             )}
//         </div>

//         {/* Footer - Follow-up Question Input */}
//         <div className="border-t p-4">
//           <div className="flex items-center gap-2 mb-3">
//             <input
//               type="text"
//               className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
//               placeholder="Ask AI to describe more or refine the response..."
//               value={followUpQuestion}
//               onChange={(e) => setFollowUpQuestion(e.target.value)}
//               onKeyPress={(e) => {
//                 if (e.key === "Enter") {
//                   e.preventDefault();
//                   handleFollowUpSubmit();
//                 }
//               }}
//               disabled={isLoading}
//             />
//             <button
//               onClick={handleFollowUpSubmit}
//               className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition disabled:opacity-50 disabled:cursor-not-allowed"
//               disabled={!followUpQuestion.trim() || isLoading}
//             >
//               <i className="bi bi-send-fill"></i>
//             </button>
//           </div>

//           {/* Action Buttons */}
//           <div className="flex justify-end gap-2">
//             <button
//               onClick={handleClose}
//               className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition text-sm"
//             >
//               Cancel
//             </button>
//             <button
//               onClick={handleSendToChat}
//               className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition disabled:opacity-50 disabled:cursor-not-allowed text-sm"
//               disabled={!selectedResponse}
//             >
//               <i className="bi bi-check-lg mr-1"></i>
//               Use This Response
//             </button>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default AIAssistantModal;
